/**
 * Settings → Custom Fields (Section 4B).
 *
 * HR admins define the extra profile fields their org wants to track.
 * Each row shows name/key, type, visibility + editability flags, and
 * category. The "New field" modal covers all field-definition options
 * plus a dropdown-options editor and a live preview of how the field
 * will render on the employee profile.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Pencil, Plus, Trash2, X } from "lucide-react";

import { useToast } from "@/hooks/useToast";

import {
  createDefinition,
  deleteDefinition,
  listDefinitions,
  updateDefinition,
  type CustomFieldCreateInput,
  type CustomFieldDefinition,
  type FieldType,
} from "@/api/customFields";

import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { Badge } from "@/components/ui/Badge";
import { Modal } from "@/components/ui/Modal";

const FIELD_TYPES: { value: FieldType; label: string }[] = [
  { value: "text", label: "Text" },
  { value: "textarea", label: "Long text" },
  { value: "number", label: "Number" },
  { value: "dropdown", label: "Dropdown" },
  { value: "date", label: "Date" },
  { value: "checkbox", label: "Checkbox" },
  { value: "email", label: "Email" },
  { value: "phone", label: "Phone" },
];

const CATEGORIES = [
  { value: "general", label: "General" },
  { value: "work", label: "Work" },
  { value: "personal", label: "Personal" },
  { value: "compliance", label: "Compliance" },
];

function slugify(s: string): string {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9_]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .replace(/_+/g, "_")
    .slice(0, 100);
}

export function CustomFieldsSettings() {
  const { addToast } = useToast();
  const [fields, setFields] = useState<CustomFieldDefinition[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<CustomFieldDefinition | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      setFields(await listDefinitions(true));
    } catch (e: any) {
      addToast(
        "error",
        `Couldn't load custom fields: ${e?.response?.data?.detail ?? "Try again."}`,
      );
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const handleDelete = async (f: CustomFieldDefinition) => {
    if (
      !window.confirm(
        `Deactivate "${f.field_name}"? Existing values are preserved.`,
      )
    )
      return;
    try {
      await deleteDefinition(f.id);
      addToast("success", "Field deactivated");
      refresh();
    } catch (e: any) {
      addToast(
        "error",
        `Delete failed: ${e?.response?.data?.detail ?? "Try again."}`,
      );
    }
  };

  const grouped = useMemo(() => {
    const m = new Map<string, CustomFieldDefinition[]>();
    for (const f of fields) {
      const arr = m.get(f.category) ?? [];
      arr.push(f);
      m.set(f.category, arr);
    }
    return [...m.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [fields]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Custom fields</h3>
          <p className="text-sm text-text-muted">
            Extra data you want to capture on employee profiles.
          </p>
        </div>
        <Button
          onClick={() => {
            setEditing(null);
            setModalOpen(true);
          }}
        >
          <Plus className="h-4 w-4" /> New field
        </Button>
      </div>

      {loading ? (
        <Card className="text-sm text-text-muted">Loading…</Card>
      ) : fields.length === 0 ? (
        <Card className="text-sm text-text-muted">
          No custom fields yet. Add one to start capturing extra profile data.
        </Card>
      ) : (
        grouped.map(([category, rows]) => (
          <Card key={category}>
            <div className="flex items-center gap-2 mb-3">
              <h4 className="text-sm font-semibold capitalize">{category}</h4>
              <span className="text-xs text-text-muted">
                {rows.length} field{rows.length === 1 ? "" : "s"}
              </span>
            </div>
            <div className="overflow-x-auto -mx-2">
              <table className="w-full text-sm">
                <thead className="text-left text-xs text-text-muted">
                  <tr>
                    <th className="py-2 px-2 font-medium">Name</th>
                    <th className="py-2 px-2 font-medium">Key</th>
                    <th className="py-2 px-2 font-medium">Type</th>
                    <th className="py-2 px-2 font-medium">Required</th>
                    <th className="py-2 px-2 font-medium">Employee can…</th>
                    <th className="py-2 px-2 font-medium">Status</th>
                    <th className="py-2 px-2 font-medium"></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((f) => (
                    <tr key={f.id} className="border-t border-border">
                      <td className="py-2 px-2 font-medium">{f.field_name}</td>
                      <td className="py-2 px-2">
                        <code className="text-xs text-text-muted">
                          {f.field_key}
                        </code>
                      </td>
                      <td className="py-2 px-2">
                        <Badge variant="info">{f.field_type}</Badge>
                      </td>
                      <td className="py-2 px-2">
                        {f.is_required ? (
                          <Badge variant="warning">Required</Badge>
                        ) : (
                          <span className="text-xs text-text-muted">Optional</span>
                        )}
                      </td>
                      <td className="py-2 px-2 text-xs">
                        <div>
                          See:{" "}
                          {f.is_visible_to_employee ? (
                            <span className="text-success">Yes</span>
                          ) : (
                            <span className="text-text-muted">No</span>
                          )}
                        </div>
                        <div>
                          Edit:{" "}
                          {f.is_editable_by_employee ? (
                            <span className="text-success">Yes</span>
                          ) : (
                            <span className="text-text-muted">No</span>
                          )}
                        </div>
                      </td>
                      <td className="py-2 px-2">
                        <Badge variant={f.is_active ? "success" : "default"}>
                          {f.is_active ? "Active" : "Inactive"}
                        </Badge>
                      </td>
                      <td className="py-2 px-2 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setEditing(f);
                              setModalOpen(true);
                            }}
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDelete(f)}
                            disabled={!f.is_active}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        ))
      )}

      {modalOpen && (
        <FieldModal
          field={editing}
          onClose={() => setModalOpen(false)}
          onSaved={async () => {
            setModalOpen(false);
            await refresh();
          }}
        />
      )}
    </div>
  );
}

// ─────────────────────────── Modal ───────────────────────────

function FieldModal({
  field,
  onClose,
  onSaved,
}: {
  field: CustomFieldDefinition | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { addToast } = useToast();
  const [fieldName, setFieldName] = useState(field?.field_name ?? "");
  const [fieldKey, setFieldKey] = useState(field?.field_key ?? "");
  const [keyTouched, setKeyTouched] = useState(Boolean(field));
  const [fieldType, setFieldType] = useState<FieldType>(
    field?.field_type ?? "text",
  );
  const [options, setOptions] = useState<string[]>(field?.options ?? []);
  const [isRequired, setIsRequired] = useState(field?.is_required ?? false);
  const [visibleToEmployee, setVisibleToEmployee] = useState(
    field?.is_visible_to_employee ?? true,
  );
  const [editableByEmployee, setEditableByEmployee] = useState(
    field?.is_editable_by_employee ?? false,
  );
  const [category, setCategory] = useState(field?.category ?? "general");
  const [isActive, setIsActive] = useState(field?.is_active ?? true);
  const [saving, setSaving] = useState(false);
  const [newOption, setNewOption] = useState("");

  // Auto-slug the key when the user hasn't manually edited it yet.
  useEffect(() => {
    if (!keyTouched && fieldName) {
      setFieldKey(slugify(fieldName));
    }
  }, [fieldName, keyTouched]);

  const addOption = () => {
    const trimmed = newOption.trim();
    if (!trimmed) return;
    if (options.includes(trimmed)) {
      addToast("error", "Option already exists");
      return;
    }
    setOptions([...options, trimmed]);
    setNewOption("");
  };

  const save = async () => {
    if (!fieldName.trim()) {
      addToast("error", "Name is required");
      return;
    }
    if (!fieldKey.trim()) {
      addToast("error", "Key is required");
      return;
    }
    if (fieldType === "dropdown" && options.length === 0) {
      addToast("error", "Dropdown fields need at least one option");
      return;
    }
    setSaving(true);
    try {
      const payload: CustomFieldCreateInput = {
        field_name: fieldName.trim(),
        field_key: fieldKey.trim(),
        field_type: fieldType,
        options: fieldType === "dropdown" ? options : null,
        is_required: isRequired,
        is_visible_to_employee: visibleToEmployee,
        is_editable_by_employee: editableByEmployee,
        category,
        is_active: isActive,
      };
      if (field) {
        await updateDefinition(field.id, payload);
      } else {
        await createDefinition(payload);
      }
      addToast("success", field ? "Field updated" : "Field created");
      onSaved();
    } catch (e: any) {
      addToast(
        "error",
        `Save failed: ${e?.response?.data?.detail ?? "Try again."}`,
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={field ? "Edit field" : "New custom field"}
      size="lg"
    >
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Input
            label="Display name"
            value={fieldName}
            onChange={(e) => setFieldName(e.target.value)}
            placeholder="e.g. T-Shirt Size"
          />
          <Input
            label="Key"
            value={fieldKey}
            onChange={(e) => {
              setKeyTouched(true);
              setFieldKey(e.target.value);
            }}
            placeholder="tshirt_size"
            disabled={Boolean(field)}
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Select
            label="Type"
            value={fieldType}
            onChange={(e) => setFieldType(e.target.value as FieldType)}
            options={FIELD_TYPES}
            disabled={Boolean(field)}
          />
          <Select
            label="Category"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            options={CATEGORIES}
          />
        </div>

        {fieldType === "dropdown" && (
          <div className="space-y-2">
            <label className="block text-sm font-medium">Options</label>
            <div className="flex flex-wrap gap-2">
              {options.map((opt) => (
                <span
                  key={opt}
                  className="inline-flex items-center gap-1 rounded-full bg-surface-muted px-3 py-1 text-xs"
                >
                  {opt}
                  <button
                    type="button"
                    onClick={() => setOptions(options.filter((o) => o !== opt))}
                    className="text-text-muted hover:text-danger"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <Input
                value={newOption}
                onChange={(e) => setNewOption(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addOption();
                  }
                }}
                placeholder="Add option…"
                className="flex-1"
              />
              <Button variant="secondary" onClick={addOption}>
                Add
              </Button>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
          <Checkbox
            label="Required"
            checked={isRequired}
            onChange={setIsRequired}
          />
          <Checkbox
            label="Visible to employee"
            checked={visibleToEmployee}
            onChange={setVisibleToEmployee}
          />
          <Checkbox
            label="Editable by employee"
            checked={editableByEmployee}
            onChange={setEditableByEmployee}
            disabled={!visibleToEmployee}
            help={!visibleToEmployee ? "Requires 'visible to employee'" : undefined}
          />
          <Checkbox label="Active" checked={isActive} onChange={setIsActive} />
        </div>

        <div className="rounded-lg border border-border bg-surface-muted p-4">
          <p className="mb-2 text-xs font-medium text-text-muted">Preview</p>
          <FieldPreview
            label={fieldName || "Field name"}
            type={fieldType}
            options={options}
          />
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={save} disabled={saving}>
            {saving ? "Saving…" : field ? "Save changes" : "Create field"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function Checkbox({
  label,
  checked,
  onChange,
  disabled,
  help,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
  help?: string;
}) {
  return (
    <label
      className={`flex items-start gap-2 ${
        disabled ? "opacity-50" : "cursor-pointer"
      }`}
    >
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5"
      />
      <span>
        {label}
        {help && <span className="block text-xs text-text-muted">{help}</span>}
      </span>
    </label>
  );
}

function FieldPreview({
  label,
  type,
  options,
}: {
  label: string;
  type: FieldType;
  options: string[];
}) {
  if (type === "textarea") {
    return (
      <div>
        <label className="block text-sm font-medium mb-1">{label}</label>
        <textarea
          disabled
          className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm"
          rows={3}
          placeholder="Multi-line text…"
        />
      </div>
    );
  }
  if (type === "checkbox") {
    return (
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" disabled />
        {label}
      </label>
    );
  }
  if (type === "dropdown") {
    return (
      <Select
        label={label}
        disabled
        options={
          options.length > 0
            ? options.map((o) => ({ value: o, label: o }))
            : [{ value: "", label: "(no options yet)" }]
        }
      />
    );
  }
  const inputType =
    type === "number"
      ? "number"
      : type === "date"
        ? "date"
        : type === "email"
          ? "email"
          : type === "phone"
            ? "tel"
            : "text";
  return (
    <Input
      label={label}
      disabled
      type={inputType}
      placeholder={`Sample ${type}…`}
    />
  );
}
